import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { TranslationService } from '@trimble-gcs/i18n';
import {
  ModusButtonModule,
  ModusFormFieldModule,
  ModusIconModule,
  ModusInputModule,
  ModusMenuModule,
  ModusSelectModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { AutoFocusDirective } from '@trimble-gcs/ngx-common';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs';
import { ClipBox } from '../../connect-3d-ext/clip-box/clip-box.models';
import { ClipBoxState } from '../../connect-3d-ext/clip-box/clip-box.state';
import { ErrorState } from '../../error-handling/error.state';
import { FeatureFlagKey } from '../../feature-flags/feature-flag-key';
import {
  ScandataEmptyComponent,
  ScandataEmptyReason,
} from '../../scandata-list/scandata-empty/scandata-empty.component';
import { ScandataSortMenuComponent } from '../../scandata-list/scandata-sort-menu/scandata-sort-menu.component';
import { ScandataTreeViewComponent } from '../../scandata-tree/scandata-tree-view/scandata-tree-view.component';
import { SortInfo } from '../../scandata/scandata-query.models';
import { sortScandata } from '../../scandata/scandata-sort';
import { SetSortInfo, SetTextFilter } from '../../scandata/scandata.actions';
import { ScandataModel } from '../../scandata/scandata.models';
import { ScandataState } from '../../scandata/scandata.state';
import { ClearCurrentStation } from '../../station/station.actions';
import { StationDisplayStatus } from '../../station/station.models';
import { StationState } from '../../station/station.state';
import { Scan3dListFilterOption } from '../models/scan-3d-list-filter-option';
import { scan3dListTranslations } from './scan-3d-list.translations';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusFormFieldModule,
    ModusSelectModule,
    ModusButtonModule,
    ModusIconModule,
    ModusInputModule,
    ModusMenuModule,
    ModusTooltipModule,
    ScandataTreeViewComponent,
    ScandataSortMenuComponent,
    ScandataEmptyComponent,
    ScrollingModule,
    MatSelectModule,
    MatProgressBarModule,
    AutoFocusDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scan-3d-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dListComponent {
  // The store select data crosses component bounds and is kept in state.
  // It is set up at the entry point into the 3D extension and consumed here.
  private readonly featureFlagService = inject(FeatureFlagService<FeatureFlagKey>);
  private readonly store = inject(Store);
  private readonly translationService = inject(TranslationService);

  readonly quotaExceeded = input(false);

  readonly settingsClick = output<void>();
  readonly modelDetailClick = output<ScandataModel>();
  readonly clipAndSaveClick = output<{ pointcloudIds: string[]; clipBox: ClipBox }>();

  private readonly currentStation = toSignal(this.store.select(StationState.currentStation));

  readonly currentStationTooltip = computed(() =>
    this.translationService.translate(this.translations.station.name.tooltip.text, {
      stationName: this.currentStation()?.station?.name ?? '',
    }),
  );

  readonly currentStationLabel = computed(() =>
    this.translationService.translate(this.translations.station.name.label.text, {
      stationName: this.currentStation()?.station?.name ?? '',
    }),
  );

  readonly hasCurrentStation = computed(
    () => this.currentStation()?.displayStatus === StationDisplayStatus.Displayed,
  );

  readonly isLoading = toSignal(this.store.select(ScandataState.isLoading), {
    initialValue: false,
  });

  readonly isSaving = toSignal(this.store.select(ScandataState.isSaving), {
    initialValue: false,
  });

  readonly scanLoadError = toSignal(this.store.select(ErrorState.hasError('scanLoadError')), {
    initialValue: false,
  });

  readonly data = computed(() => {
    const scans = this.filteredScandata().filter(
      (x) => this.filterOptionControlValue() === Scan3dListFilterOption.ShowAll || x.selected,
    );

    const sortInfo = this.sortInfo();
    return sortScandata(scans, sortInfo.sortBy, sortInfo.sortDirection);
  });

  readonly filterOptionControl = new FormControl<Scan3dListFilterOption>(
    Scan3dListFilterOption.ShowAll,
  );

  private readonly filterOptionControlValue = toSignal(this.filterOptionControl.valueChanges, {
    initialValue: null,
  });

  private readonly filteredScandata = toSignal(
    this.store.select(ScandataState.textFilteredScandata),
    {
      initialValue: [],
    },
  );

  readonly scan3dListFilterOption = Scan3dListFilterOption;

  private readonly textFilter = toSignal(this.store.select(ScandataState.textFilter));
  readonly showTextFilter = signal(false);
  readonly textFilterControl = new FormControl<string | null>(null);

  readonly sortInfo = toSignal(this.store.select(ScandataState.sortInfo), { requireSync: true });

  private readonly filterCount = toSignal(this.store.select(ScandataState.filterCount), {
    initialValue: 0,
  });

  readonly scandataEmptyReason = computed(() =>
    !this.showScandataEmpty() ? ScandataEmptyReason.NoUploads : this.getScandataEmptyReason(),
  );

  readonly showScandataEmpty = computed(() =>
    this.isLoading() || this.scanLoadError() ? false : this.data().length === 0,
  );

  readonly translations = scan3dListTranslations();

  readonly hasRotatePanoramasFeature = toSignal(
    this.featureFlagService.hasFeature$('3d_rotatePanoramas'),
    {
      initialValue: false,
    },
  );

  readonly hasViewInNumarkFeature = toSignal(this.featureFlagService.hasFeature$('viewInNumark'), {
    initialValue: false,
  });

  readonly hasClipAndSaveFeature = toSignal(this.featureFlagService.hasFeature$('clipAndSave'), {
    initialValue: false,
  });

  private readonly clipBox = toSignal(this.store.select(ClipBoxState.clipBox));
  private readonly displayedScans = toSignal(this.store.select(ScandataState.displayed), {
    initialValue: [] as ScandataModel[],
  });

  readonly showClipboxBanner = computed(
    () =>
      this.hasClipAndSaveFeature() &&
      isDefined(this.clipBox()) &&
      this.displayedScans().length > 0,
  );

  constructor() {
    this.subscribeToSelected();
    this.subscribeToTextFilterChanges();
  }

  hideTextFilterClick() {
    this.showTextFilter.set(false);
    this.textFilterControl.reset();
  }

  onExitStationClick() {
    this.store.dispatch(new ClearCurrentStation());
  }

  onModelDetailClick(model: ScandataModel) {
    this.modelDetailClick.emit(model);
  }

  onSettingClick() {
    this.settingsClick.emit();
  }

  showTextFilterClick() {
    this.showTextFilter.set(true);
  }

  sortClick(sortInfo: SortInfo) {
    this.store.dispatch(new SetSortInfo(sortInfo));
  }

  onClipAndSaveClick() {
    if (!this.hasClipAndSaveFeature()) return;

    const clipBox = this.clipBox();
    const pointcloudIds = this.displayedScans().map((scan) => scan.id);
    if (!isDefined(clipBox) || pointcloudIds.length === 0) return;

    this.clipAndSaveClick.emit({ pointcloudIds, clipBox });
  }

  private getScandataEmptyReason() {
    const filterCount = this.filterCount();
    const textFilter = this.textFilter();

    if (filterCount > 0 || isDefined(textFilter)) return ScandataEmptyReason.NoFilterResults;

    if (this.filterOptionControlValue() === Scan3dListFilterOption.ShowSelected)
      return ScandataEmptyReason.NoSelected;

    return ScandataEmptyReason.NoUploads;
  }

  private subscribeToSelected() {
    this.store
      .select(ScandataState.selected)
      .pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length),
        map((selected) => {
          return selected.length > 0
            ? Scan3dListFilterOption.ShowSelected
            : Scan3dListFilterOption.ShowAll;
        }),
        untilDestroyed(this),
      )
      .subscribe((option) => {
        return this.filterOptionControl.setValue(option);
      });
  }

  private subscribeToTextFilterChanges() {
    this.textFilterControl.valueChanges
      .pipe(
        debounceTime(250),
        switchMap((textFilter) => this.store.dispatch(new SetTextFilter(textFilter ?? undefined))),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
